import { Switch, TextField, Label, Select, Tag } from '@loadsmart/loadsmart-ui'
import FileViewer from 'molecules/FileViewer/FileViewer'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Box,
  Stack,
  Typography,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon, HelpOutline, Dashboard, Settings } from '@mui/icons-material'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import LSDate from 'common/Date.helpers'
import { MessageDirection } from 'common/types/kraken-core/Message'
import { TranslationMapLoaderEnum } from 'common/types/kraken-core/TranslationMap'
import { getLoaderMapping } from 'common/helpers/TranslationMapLoader.helpers'
import isEmpty from 'lodash.isempty'
import { useCallback, useMemo, memo } from 'react'
import { useSearchLoaders } from '../api'
import { useTranslationMapDetailsContext } from '../contexts/TranslationMapDetailsContext'

export interface TranslationMapDetailsFormProps {}

// Helper component for field labels with tooltips
export const FieldLabel = ({
  field,
  required = false,
  children,
}: {
  field: string
  required?: boolean
  children: React.ReactNode
}) => {
  const fieldHelp: Record<string, string> = {
    name: 'A unique identifier for this translation map',
    code: 'Internal code used for programmatic reference',
    loader: 'The processing engine that will execute this translation',
    direction: 'Whether this translation processes inbound or outbound messages',
    input_data_model: 'Schema defining the structure of input data',
    output_data_model: 'Schema defining the structure of output data',
    input_content_type: 'MIME type of the input data format',
    output_content_type: 'MIME type of the output data format',
    input_encoding: 'Character encoding of the input data',
    output_encoding: 'Character encoding of the output data',
    output_name_pattern: 'Pattern for naming output files',
    path: 'File system path for this translation map',
    output_headers: 'Headers to include in the output',
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Label required={required}>{children}</Label>
      <Tooltip title={fieldHelp[field] || ''}>
        <HelpOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  )
}

const DIRECTION_FILTER_VALUES = [
  {
    label: 'Inbound',
    value: MessageDirection.INBOUND,
  },
  {
    label: 'Outbound',
    value: MessageDirection.OUTBOUND,
  },
]

const GeneralDetails = memo(function GeneralDetails(_props: TranslationMapDetailsFormProps) {
  const {
    translationMap,
    updateTranslationMap,
    viewMode,
    setViewMode,
  } = useTranslationMapDetailsContext()

  const { data: loadersList } = useSearchLoaders()

  // Early return if required values are not available
  if (!translationMap) {
    return null
  }

  const handleChange = useCallback(
    (key: any, value: any) => {
      console.log('GeneralDetails handleChange called with key:', key, 'value:', value)
      console.log('GeneralDetails current translationMap:', translationMap)
      if (translationMap && updateTranslationMap) {
        console.log('GeneralDetails calling updateTranslationMap with:', { [key]: value })
        updateTranslationMap({ [key]: value })
      }
    },
    [translationMap, updateTranslationMap]
  )

  const isPathInputDisabled = translationMap.loader
    ? translationMap.loader.startsWith(TranslationMapLoaderEnum.RUNTIME_DATABASE_LOADER)
    : false

  const LoaderSelector = loadersList ? (
    <Select
      id="loader"
      name="loader"
      onChange={e => {
        if (e.target.value) handleChange('loader', (e.target.value as any).value)
        else handleChange('loader', undefined)
      }}
      options={loadersList.map(loader => {
        const mapping = getLoaderMapping(loader.loader)
        return {
          label: `${mapping.name} - ${loader.description}`,
          value: loader.loader,
        }
      })}
      value={
        {
          value: translationMap?.loader || '-',
          label: translationMap?.loader
            ? `${getLoaderMapping(translationMap.loader).name} - ${
                loadersList.find(p => p.loader === translationMap?.loader)?.description || '-'
              }`
            : '-',
        } as Selectable
      }
    />
  ) : null

  const DirectionSelector = useMemo(() => {
    return (
      <Select
        id="use_when_direction"
        name="use_when_direction"
        options={DIRECTION_FILTER_VALUES}
        onChange={e => handleChange('use_when_direction', (e.target.value as any).value)}
        value={
          {
            value: translationMap?.use_when_direction || '-',
            label:
              DIRECTION_FILTER_VALUES.find(p => p.value === translationMap?.use_when_direction)
                ?.label || '-',
          } as Selectable
        }
      />
    )
  }, [translationMap?.use_when_direction, handleChange])

  return (
    <Grid container spacing={2}>
      {/* Basic Information Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {/* Basic Information Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                {/* Left side - Name and Direction */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' },
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Box>
                    <Stack spacing={0.5}>
                      <FieldLabel field="name" required>
                        Name
                      </FieldLabel>
                      <TextField
                        value={translationMap.name}
                        status={isEmpty(translationMap.name) ? ('danger' as any) : null}
                        onChange={e => handleChange('name', e.target.value)}
                        placeholder="Enter translation map name"
                      />
                    </Stack>
                  </Box>
                  <Box>
                    <Stack spacing={0.5}>
                      <FieldLabel field="direction" required>
                        Direction
                      </FieldLabel>
                      {DirectionSelector}
                    </Stack>
                  </Box>
                </Box>

                {/* Right side - Reuse original message (advanced only) and View Mode */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    flexShrink: 0,
                  }}
                >
                  {viewMode === 'advanced' && (
                    <Box>
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Stack spacing={0.5}>
                            <Label>Reuse original message</Label>
                            <Switch
                              active={!!translationMap?.output_reuse_original_message}
                              onToggle={e =>
                                handleChange('output_reuse_original_message', e.target.checked)
                              }
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Stack spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                      <Label>View Mode</Label>
                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newMode) => newMode && setViewMode?.(newMode)}
                        size="small"
                        sx={{
                          '& .MuiToggleButton-root': {
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            minHeight: 'auto',
                          },
                        }}
                      >
                        <ToggleButton value="simple">
                          <Dashboard sx={{ mr: 0.25, fontSize: '0.9rem' }} />
                          Simple
                        </ToggleButton>
                        <ToggleButton value="advanced">
                          <Settings sx={{ mr: 0.25, fontSize: '0.9rem' }} />
                          Advanced
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Box>
                </Box>
              </Box>

              {/* Code Field - Full Width in Advanced Mode */}
              {viewMode === 'advanced' && (
                <Box>
                  <Stack spacing={0.5}>
                    <FieldLabel field="code">Code</FieldLabel>
                    <TextField
                      value={translationMap.code}
                      onChange={e => handleChange('code', e.target.value)}
                      placeholder="Enter translation code"
                    />
                  </Stack>
                </Box>
              )}

              {/* Execution Configuration - Only visible in Advanced view */}
              {viewMode === 'advanced' && (
                <>
                  <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', fontSize: '0.9rem' }}
                    >
                      Execution Configuration
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                        gap: 1.5,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box>
                        <Stack spacing={0.5}>
                          <FieldLabel field="loader" required>
                            Loader
                          </FieldLabel>
                          {LoaderSelector}
                        </Stack>
                      </Box>
                      <Box>
                        <Stack spacing={0.5}>
                          <FieldLabel field="output_name_pattern">Name Pattern</FieldLabel>
                          <TextField
                            value={translationMap.output_name_pattern || ''}
                            onChange={e => handleChange('output_name_pattern', e.target.value)}
                            placeholder="e.g., {timestamp}_{partner}_{type}"
                          />
                        </Stack>
                      </Box>
                      <Box>
                        <Stack spacing={0.5}>
                          <FieldLabel field="path">Path</FieldLabel>
                          <TextField
                            value={translationMap.path || ''}
                            disabled={isPathInputDisabled}
                            onChange={e => handleChange('path', e.target.value)}
                            placeholder="/path/to/translation"
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </Box>

                  {/* Metadata Section - Only visible in Advanced view */}
                  {(translationMap.id || translationMap.deleted_at) && (
                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: 'text.secondary',
                          fontSize: '0.9rem',
                        }}
                      >
                        Metadata
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(auto-fit, minmax(150px, 1fr))',
                          },
                          gap: 1.5,
                        }}
                      >
                        {translationMap.id && (
                          <Box>
                            <Stack spacing={0.5}>
                              <Label>Created</Label>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                              >
                                {LSDate(translationMap.created_at)?.format('MM/DD/YY HH:mm')}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                        {translationMap.id && (
                          <Box>
                            <Stack spacing={0.5}>
                              <Label>Updated</Label>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                              >
                                {LSDate(translationMap.updated_at)?.format('MM/DD/YY HH:mm')}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                        {translationMap.deleted_at && (
                          <Box>
                            <Stack spacing={0.5}>
                              <Label>Deleted</Label>
                              <Typography
                                variant="body2"
                                sx={{ color: 'error.main', fontSize: '0.8rem' }}
                              >
                                {LSDate(translationMap.deleted_at)?.format('MM/DD/YY HH:mm')}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Box>

                      {/* Use When Section - Inside Metadata */}
                      <Box sx={{ mt: 1.5 }}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem' }}>
                                Use when
                              </Typography>
                              <Tag variant="warning">Deprecated</Tag>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <FileViewer
                              options={{
                                heigth: 200,
                              }}
                              content={JSON.stringify(translationMap.use_when_facts, null, 2)}
                              contentType="application/json"
                              showOptions={false}
                              onChange={value =>
                                handleChange('use_when_facts', JSON.parse(value || '{}'))
                              }
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
})

export default GeneralDetails
